// ─────────────────────────────────────────────────────────────
//  i18n / LanguageContext.js
//
//  Kullanım:
//    const { t, tList, lang, setLang } = useLanguage();
//    t("auth.login")                        → "Giriş Yap"
//    t("home.greeting", { ad: "Ali" })      → interpolasyon
//    tList("const.roles")                   → ["Anne", "Baba", ...]
//
//  Capacitor notu:
//    localStorage web ve native WebView'de aynı şekilde çalışır.
//    Firebase sync useAuth içinde yapılır (bu dosya saf React).
// ─────────────────────────────────────────────────────────────

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import tr from "./tr";
import en from "./en";

/* ── Desteklenen diller ── */
export const LANGUAGES = ["tr", "en"];
export const LANGUAGE_LABELS = { tr: "TR", en: "EN" };

const DICTIONARIES = { tr, en };
const LS_KEY = "appLanguage";

export function detectSupportedDeviceLanguage(navigatorLike = typeof navigator !== "undefined" ? navigator : null) {
  const candidates = [
    ...(Array.isArray(navigatorLike?.languages) ? navigatorLike.languages : []),
    navigatorLike?.language,
    navigatorLike?.userLanguage,
  ].filter(Boolean);
  for (const candidate of candidates) {
    const base = String(candidate).trim().toLowerCase().split(/[-_]/)[0];
    if (LANGUAGES.includes(base)) return base;
  }
  return "tr";
}

/* ── localStorage'dan başlangıç dilini oku, yoksa cihaz dilini algıla ── */
function getInitialLang() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored && LANGUAGES.includes(stored)) return stored;
  } catch {
    // localStorage erişim hatası (private mod vb.) → default
  }
  // Kullanıcı uygulama içinde dili hiç değiştirmediyse (ör. ilk açılış, henüz
  // giriş yapmamış kullanıcı) cihaz/tarayıcı diline bakarak makul bir varsayım
  // yap. Bu özellikle "Şifremi Unuttum" gibi giriş öncesi akışlarda gönderilen
  // e-postaların doğru dilde gitmesi için önemli — aksi halde her zaman "tr"
  // varsayılırdı.
  //
  // ÖNEMLİ: Burada bulunan değer yalnızca bu fonksiyonun döndürdüğü değer
  // üzerinden React state'ine değil, localStorage'a da YAZILMALI. Aksi halde
  // ekran doğru dilde görünse bile (Context state doğru), useAuth.js içindeki
  // sifremiUnuttum/kayitOl/dogrulamaMailiTekrarGonder gibi fonksiyonlar
  // localStorage'ı DOĞRUDAN okuduğu için hâlâ boş/varsayılan değeri görür ve
  // "tr" gönderir — ekran İngilizce ama gönderilen mail Türkçe olur.
  try {
    const detected = detectSupportedDeviceLanguage(navigator);
    try { localStorage.setItem(LS_KEY, detected); } catch {}
    return detected;
  } catch {
    // navigator erişilemezse default
  }
  return "tr";
}

/* ── Context ── */
export const LanguageContext = createContext(null);

/* ── Provider ── */
export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  /* Dili değiştir: localStorage + state */
  const setLang = useCallback((newLang) => {
    if (!LANGUAGES.includes(newLang)) return;
    try {
      localStorage.setItem(LS_KEY, newLang);
    } catch {}
    setLangState(newLang);
  }, []);

  const dict = DICTIONARIES[lang] ?? DICTIONARIES["tr"];

  /* t(key, vars?) → string */
  const t = useCallback(
    (key, vars) => {
      const val = dict[key];
      if (val === undefined) {
        // Anahtar bulunamazsa TR fallback, sonra key'in kendisi
        const fallback = DICTIONARIES["tr"][key];
        if (typeof fallback === "string") return interpolate(fallback, vars);
        return key;
      }
      if (typeof val !== "string") return key; // liste gibi tipler için tList kullanın
      return interpolate(val, vars);
    },
    [dict]
  );

  /* tList(key) → dizi (sabitler, kategoriler vb.) */
  const tList = useCallback(
    (key) => {
      const val = dict[key];
      if (Array.isArray(val)) return val;
      // Fallback: TR
      const fallback = DICTIONARIES["tr"][key];
      if (Array.isArray(fallback)) return fallback;
      return [];
    },
    [dict]
  );

  const value = useMemo(
    () => ({ t, tList, lang, setLang }),
    [t, tList, lang, setLang]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/* ── Hook ── */
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used inside <LanguageProvider>");
  }
  return ctx;
}

/* ── Yardımcı: {{değişken}} interpolasyonu ── */
function interpolate(str, vars) {
  if (!vars || typeof vars !== "object") return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    vars[key] !== undefined ? vars[key] : `{{${key}}}`
  );
}
